// ─── App Route (Protected Layout) ───
// Requires authentication. Renders AppShell with Outlet for child routes.
// If not authenticated and not loading, navigates to /login preserving the
// deep-link destination via `?returnTo=`.

import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { AppShell } from '../features/layout/AppShell';
import { useAuth } from '../features/auth/AuthProvider';

export const Route = createFileRoute('/app')({
  component: AppLayout,
});

function AppLayout(): React.JSX.Element | null {
  const { isAuthenticated, isLoading } = useAuth();

  // While checking auth state, show a loading spinner
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-bg">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated — navigate to login, preserving the deep-link
  // destination. NP-P65a: a hard `window.location.href` redirect here would
  // lose the destination and cause a full reload; TanStack Router's
  // `<Navigate>` keeps the SPA context and passes the destination through
  // the `returnTo` search param instead. Never render AppShell while
  // unauthenticated.
  if (!isAuthenticated) {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return <Navigate to="/login" search={{ returnTo }} replace />;
  }

  return <AppShell />;
}
